import React, { useState } from "react";
import { Link } from "react-router-dom";
import BookDemoModal from "../components/BookDemoModal";
import {
  Search,
  Book,
  MessageCircle,
  Mail,
  Phone,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  FileText,
  Video,
  Users,
  Settings,
  Shield,
  Zap,
} from "lucide-react";

const HelpCenter: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const categories = [
    {
      icon: Book,
      title: "Getting Started",
      description: "Learn the basics of VetriaAI",
      articles: 12,
      color: "bg-blue-100 text-blue-600",
    },
    {
      icon: Users,
      title: "Account Management",
      description: "Manage your account and team",
      articles: 8,
      color: "bg-green-100 text-green-600",
    },
    {
      icon: Settings,
      title: "Test Creation",
      description: "Create and customize assessments",
      articles: 15,
      color: "bg-purple-100 text-purple-600",
    },
    {
      icon: Shield,
      title: "Security & Privacy",
      description: "Data protection and compliance",
      articles: 6,
      color: "bg-red-100 text-red-600",
    },
    {
      icon: Zap,
      title: "Integrations",
      description: "Connect with your existing tools",
      articles: 10,
      color: "bg-yellow-100 text-yellow-600",
    },
    {
      icon: MessageCircle,
      title: "Troubleshooting",
      description: "Common issues and solutions",
      articles: 9,
      color: "bg-gray-100 text-gray-600",
    },
  ];

  const popularArticles = [
    {
      title: "How to create your first AI-generated test",
      category: "Getting Started",
      readTime: "5 min read",
      views: "2.1k views",
    },
    {
      title: "Understanding candidate assessment results",
      category: "Test Creation",
      readTime: "3 min read",
      views: "1.8k views",
    },
    {
      title: "Setting up team permissions and roles",
      category: "Account Management",
      readTime: "4 min read",
      views: "1.5k views",
    },
    {
      title: "Integrating VetriaAI with your ATS",
      category: "Integrations",
      readTime: "7 min read",
      views: "1.2k views",
    },
    {
      title: "Data security and GDPR compliance",
      category: "Security & Privacy",
      readTime: "6 min read",
      views: "1.1k views",
    },
  ];

  const faqs = [
    {
      question: "How does the AI test generation work?",
      answer:
        "Our AI analyzes your job description and automatically creates relevant assessments based on the required skills, experience level, and industry standards. The system uses natural language processing to understand the key competencies needed and generates appropriate questions across technical skills, problem-solving, and behavioral assessments.",
    },
    {
      question: "Can I customize the AI-generated tests?",
      answer:
        "Absolutely! While our AI creates a solid foundation, you have full control to edit questions, add your own content, adjust difficulty levels, and set custom scoring criteria. You can also save frequently used questions to your custom question bank for future use.",
    },
    {
      question: "How accurate are the assessment results?",
      answer:
        "Our assessments maintain a 95% accuracy rate through continuous machine learning improvements and validation against real-world job performance data. We regularly update our algorithms based on hiring outcomes and candidate feedback to ensure the highest quality results.",
    },
    {
      question: "What types of assessments can I create?",
      answer:
        "VetriaAI supports multiple assessment types including technical skills tests, cognitive assessments, personality evaluations, scenario-based challenges, coding challenges, and AI-powered video interviews. You can combine different types to create comprehensive evaluation experiences.",
    },
    {
      question: "Is my candidate data secure?",
      answer:
        "Yes, we take data security very seriously. All data is encrypted in transit and at rest using AES-256 encryption. We're SOC 2 Type II compliant, GDPR compliant, and undergo regular security audits. Your candidate data is never shared with third parties without explicit consent.",
    },
    {
      question: "How do I invite candidates to take assessments?",
      answer:
        "You can invite candidates through multiple methods: direct email invitations, shareable assessment links, integration with your existing ATS, or embedded widgets on your career page. Candidates receive clear instructions and can complete assessments on any device.",
    },
    {
      question: "Can I integrate VetriaAI with my existing tools?",
      answer:
        "Yes, we offer integrations with popular ATS platforms, HRIS systems, and productivity tools. Our API allows for custom integrations, and we provide webhooks for real-time data synchronization. Contact our support team for specific integration requirements.",
    },
    {
      question: "What support is available if I need help?",
      answer:
        "We offer multiple support channels: 24/7 chat support, email support, phone support for Enterprise customers, comprehensive documentation, video tutorials, and webinar training sessions. Our customer success team is also available to help with onboarding and best practices.",
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="/vetriaAI.png"
                alt="VetriaAI Logo"
                className="w-10 h-10"
              />
              <span className="text-2xl font-bold text-gray-900">VetriaAI</span>
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            <Link
              to="/login"
              className="text-gray-600 hover:text-gray-900 font-medium"
            >
              Sign In
            </Link>
            <Link
              to="/login"
              className="bg-red-800 text-white px-6 py-2 rounded-lg hover:bg-red-900 transition-colors font-medium"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-green-50 to-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            How can we
            <span className="text-green-600"> help you?</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Find answers to your questions, learn how to use VetriaAI
            effectively, and get the support you need to transform your hiring
            process.
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="w-6 h-6 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search for help articles, FAQs, or topics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg inline-flex items-center justify-center"
            >
              Sign In <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <button
              onClick={() => setIsModalOpen(true)}
              className="border border-gray-300 text-gray-700 px-8 py-4 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-lg"
            >
              Book Your Demo
            </button>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Browse by Category
            </h2>
            <p className="text-xl text-gray-600">
              Find the information you need organized by topic
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 cursor-pointer group"
                >
                  <div
                    className={`w-12 h-12 ${category.color} rounded-lg flex items-center justify-center mb-4`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">
                    {category.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{category.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {category.articles} articles
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-green-600 transition-colors" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popular Articles */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Popular Articles
            </h2>
            <p className="text-xl text-gray-600">
              Most viewed help articles from our community
            </p>
          </div>

          <div className="space-y-4">
            {popularArticles.map((article, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                      {article.title}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="px-2 py-1 bg-gray-100 rounded">
                        {article.category}
                      </span>
                      <span>{article.readTime}</span>
                      <span>{article.views}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-green-600 transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Quick answers to common questions about VetriaAI
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </h3>
                  {openFAQ === index ? (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Still Need Help?
            </h2>
            <p className="text-xl text-gray-600">
              Our support team is here to help you succeed
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm text-center border border-gray-100">
              <MessageCircle className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Live Chat
              </h3>
              <p className="text-gray-600 mb-6">
                Get instant help from our support team
              </p>
              <a 
                href="https://wa.me/918465922194" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium inline-block"
              >
                Start Chat
              </a>
              <p className="text-sm text-gray-500 mt-2">Available 24/7</p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm text-center border border-gray-100">
              <Mail className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Email Support
              </h3>
              <p className="text-gray-600 mb-6">Send us a detailed message</p>
              <a 
                href="mailto:admin@vetriaai.com" 
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium inline-block"
              >
                Send Email
              </a>
              <p className="text-sm text-gray-500 mt-2">
                Response within 2 hours
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm text-center border border-gray-100">
              <Phone className="w-12 h-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Phone Support
              </h3>
              <p className="text-gray-600 mb-6">
                Speak directly with our experts
              </p>
              <a 
                href="tel:+918465922194" 
                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium inline-block"
              >
                Call Now
              </a>
              <p className="text-sm text-gray-500 mt-2">
                Enterprise customers only
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600 px-6">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of companies using VetriaAI to transform their hiring
            process.
          </p>
          <Link
            to="/login"
            className="bg-white text-green-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg inline-flex items-center"
          >
            Sign In <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="/vetriaAI.png"
                  alt="VetriaAI Logo"
                  className="w-8 h-8"
                />
                <span className="text-xl font-bold">VetriaAI</span>
              </div>
              <p className="text-gray-400">
                Revolutionizing recruitment with AI-powered intelligence.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-900">Product</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/features" className="text-white hover:text-gray-300">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-white hover:text-gray-300">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-900">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-white hover:text-gray-300">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-white hover:text-gray-300">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-900">Support</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/help-center" className="text-white hover:text-gray-300">
                    Help Center
                  </Link>
                </li>
                <li>
                  <a href="#" className="text-white hover:text-gray-300">
                    Documentation
                  </a>
                </li>
                <li>
                  <Link to="/status" className="text-white hover:text-gray-300">
                    Status
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-900">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/cancellations-refunds" className="text-white hover:text-gray-300">
                    Cancellations &amp; Refunds
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-white hover:text-gray-300">
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/shipping" className="text-white hover:text-gray-300">
                    Shipping (Digital)
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-white hover:text-gray-300">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>2025 VetriaAI. All rights and trademarks are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>
      
      <BookDemoModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default HelpCenter;
